import { Scissors, LogOut, Settings as SettingsIcon, FileText, Zap, Menu } from "lucide-react";
import { Link } from "react-router-dom";

export default function Header({ user, onLogout, onOpenSettings, onOpenMenu, billing }) {
  const showUpgrade = billing && !billing.grandfathered && billing.plan === "free";
  const showRenewSoon = billing && !billing.grandfathered && billing.plan === "pro" && billing.days_remaining != null && billing.days_remaining <= 14;
  const showProBadge = billing && (billing.grandfathered || billing.plan === "pro" || billing.plan === "trialing") && !showRenewSoon;
  return (
    <header className="border-b border-zinc-200 bg-white" data-testid="app-header">
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {user && (
            <button
              type="button"
              onClick={onOpenMenu}
              className="lg:hidden p-1.5 -ml-1 text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
              aria-label="Open menu"
              data-testid="hamburger-btn"
            >
              <Menu className="w-5 h-5" strokeWidth={1.75} />
            </button>
          )}
          <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center shrink-0">
            <Scissors className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold tracking-tight text-zinc-900 leading-none">
              BreakItDown
            </div>
            <div className="hidden sm:block text-[10px] uppercase tracking-[0.18em] text-zinc-400 mt-1 truncate">
              Break it down. Get it done.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 lg:gap-5 shrink-0">
          <a
            href="https://charlottebryan.com.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-900 transition-colors whitespace-nowrap"
            data-testid="charlotte-bryan-link-header"
          >
            A <span className="font-semibold text-zinc-900">Charlotte Bryan</span> app ↗
          </a>

          {user && (
            <div className="flex items-center gap-2 sm:gap-3" data-testid="user-menu">
              {showUpgrade && (
                <Link
                  to="/billing"
                  className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-[0.12em] font-medium px-2.5 sm:px-3 py-1.5 bg-[#002FA7] hover:bg-zinc-900 text-white transition-colors whitespace-nowrap"
                  data-testid="header-upgrade-btn"
                  title="Upgrade to Pro"
                >
                  <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={1.75} />
                  <span className="hidden sm:inline">Upgrade</span>
                </Link>
              )}
              {showRenewSoon && (
                <Link
                  to="/billing"
                  className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-[0.12em] font-medium px-2.5 sm:px-3 py-1.5 border border-amber-500 text-amber-700 hover:bg-amber-50 transition-colors whitespace-nowrap"
                  data-testid="header-renew-btn"
                  title={`Pro renews in ${billing.days_remaining} day${billing.days_remaining === 1 ? "" : "s"}`}
                >
                  Renew · {billing.days_remaining}d
                </Link>
              )}
              {showProBadge && (
                <Link
                  to="/billing"
                  className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-medium px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-500 transition-colors whitespace-nowrap"
                  data-testid="header-pro-badge"
                  title={billing.grandfathered ? "Lifetime Pro" : billing.plan === "trialing" ? `Trial · ${billing.days_remaining}d left` : "Pro plan"}
                >
                  {billing.grandfathered ? "Pro · Lifetime" : billing.plan === "trialing" ? `Trial · ${billing.days_remaining}d` : "Pro"}
                </Link>
              )}
              {user.role === "admin" && (
                <Link
                  to="/admin/blog"
                  className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-zinc-500 hover:text-zinc-900 px-2 py-1.5 border border-zinc-300 hover:border-zinc-900 transition-colors whitespace-nowrap"
                  data-testid="admin-blog-link"
                  title="Blog admin"
                >
                  <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Blog
                </Link>
              )}
              <div className="hidden lg:flex flex-col items-end leading-tight">
                <span className="text-xs text-zinc-500">Signed in as</span>
                <span className="text-xs font-semibold text-zinc-900 max-w-[180px] truncate" data-testid="user-email">
                  {user.email}
                </span>
              </div>
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || user.email}
                  className="w-8 h-8 border border-zinc-300 object-cover shrink-0"
                />
              ) : (
                <div className="w-8 h-8 bg-zinc-100 border border-zinc-300 flex items-center justify-center text-xs font-semibold text-zinc-700 shrink-0" title={user.email}>
                  {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <button
                type="button"
                onClick={onOpenSettings}
                className="hidden lg:inline-flex p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors shrink-0"
                aria-label="Settings"
                title="Settings"
                data-testid="settings-btn"
              >
                <SettingsIcon className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={onLogout}
                className="hidden lg:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-zinc-500 hover:text-red-700 transition-colors px-2 py-1.5 border border-transparent hover:border-red-200 whitespace-nowrap shrink-0"
                data-testid="logout-btn"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="hidden lg:inline">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
